import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Save } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["nuovo", "confermato", "in_preparazione", "spedito", "consegnato", "annullato"];

export default function OrdersList() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ tracking_number: "", courier_name: "", tracking_url: "", status_logistico: "nuovo", note: "" });
  const [carriers, setCarriers] = useState([]);

  const refresh = () => api.get("/orders").then((r) => setOrders(r.data));

  useEffect(() => {
    refresh();
    api.get("/shipping/carriers").then((r) => setCarriers(r.data || []));
  }, []);

  const openEdit = (o) => {
    setEditing(o);
    setForm({
      tracking_number: o.tracking_number || "",
      courier_name: o.courier_name || "",
      tracking_url: o.tracking_url || "",
      status_logistico: o.status_logistico || "nuovo",
      note: "",
    });
  };

  const save = async () => {
    try {
      let trackingUrl = form.tracking_url;
      const carrier = carriers.find((c) => c.name === form.courier_name);
      if (!trackingUrl && carrier?.tracking_url_template && form.tracking_number) {
        trackingUrl = carrier.tracking_url_template.replace("{tracking}", form.tracking_number);
      }
      await api.put(`/orders/${editing.id}/tracking`, { ...form, tracking_url: trackingUrl });
      toast.success("Tracking aggiornato");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const role = user?.role;

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10" data-testid="orders-list">
        <div className="mb-10">
          <div className="overline text-brand mb-2">{role === "casa_madre" ? "Casa Madre" : role === "rivenditore" ? "Rivenditore" : "SuperAdmin"}</div>
          <h1 className="font-heading font-bold text-4xl tracking-tight">Ordini & Tracking</h1>
          <p className="text-muted-foreground mt-2 text-sm">Gestisci tracking, corriere e stato logistico per ogni ordine.</p>
        </div>

        <div className="bg-white border border-border rounded-sm overflow-hidden">
          {orders.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">Nessun ordine ancora.</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-border">
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">N° Ordine</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Cliente</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Corriere</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Tracking</th>
                  <th className="text-right px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground">Totale</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-border last:border-0" data-testid={`order-row-${o.numero_ordine}`}>
                    <td className="px-4 py-3 font-mono text-xs">{o.numero_ordine}</td>
                    <td className="px-4 py-3 text-sm">{o.customer?.name || o.customer?.email}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        o.status_logistico === "consegnato" ? "bg-emerald-100 text-emerald-700"
                        : o.status_logistico === "spedito" ? "bg-blue-100 text-blue-700"
                        : o.status_logistico === "annullato" ? "bg-red-100 text-red-700"
                        : "bg-accent text-foreground"
                      }`}>{o.status_logistico}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">{o.courier_name || "—"}</td>
                    <td className="px-4 py-3 text-xs font-mono">{o.tracking_number || "—"}</td>
                    <td className="px-4 py-3 text-right font-medium">€ {o.totale}</td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`order-edit-${o.numero_ordine}`} onClick={() => openEdit(o)}
                        className="text-xs px-3 h-8 rounded-sm border border-border hover:bg-accent">Modifica tracking</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent data-testid="order-tracking-dialog">
            <DialogHeader>
              <DialogTitle>Tracking ordine {editing?.numero_ordine}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-3">
              <div>
                <label className="overline text-muted-foreground mb-1 block">Status logistico</label>
                <select data-testid="order-status-select" value={form.status_logistico}
                  onChange={(e) => setForm({ ...form, status_logistico: e.target.value })}
                  className="w-full h-10 px-3 rounded-sm border border-border text-sm">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="overline text-muted-foreground mb-1 block">Corriere</label>
                <select data-testid="order-courier-select" value={form.courier_name}
                  onChange={(e) => setForm({ ...form, courier_name: e.target.value })}
                  className="w-full h-10 px-3 rounded-sm border border-border text-sm">
                  <option value="">— scegli —</option>
                  {carriers.map((c, i) => <option key={i} value={c.name}>{c.name}</option>)}
                  <option value="Custom">Custom (inserisci manualmente)</option>
                </select>
              </div>
              <div>
                <label className="overline text-muted-foreground mb-1 block">Numero tracking</label>
                <input data-testid="order-tracking-num" value={form.tracking_number}
                  onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
                  className="w-full h-10 px-3 rounded-sm border border-border text-sm" />
              </div>
              <div>
                <label className="overline text-muted-foreground mb-1 block">URL tracking (opz. auto-calcolato)</label>
                <input data-testid="order-tracking-url" value={form.tracking_url}
                  onChange={(e) => setForm({ ...form, tracking_url: e.target.value })}
                  className="w-full h-10 px-3 rounded-sm border border-border text-sm" />
              </div>
              <div>
                <label className="overline text-muted-foreground mb-1 block">Nota interna</label>
                <textarea data-testid="order-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  rows={2} className="w-full px-3 py-2 rounded-sm border border-border text-sm" />
              </div>
              <button data-testid="order-save-btn" onClick={save}
                className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-sm bg-brand hover:bg-brand-hover text-white text-sm font-medium">
                <Save className="w-4 h-4" /> Salva tracking
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
